import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { User as SupabaseUser, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

// Define the types locally to avoid import issues
interface UserRole {
  id: number;
  name: string;
  description: string | null;
  isActive: boolean;
  isSystem: boolean | null;
}

interface User {
  id: number;
  uid: string;
  name: string;
  email: string;
  avatarUrl: string;
  isActive: boolean;
  registeredAt: string;
  roleIds: number[];
  roles: UserRole[];
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  authError: string | null;
  /** Google OAuth. The browser leaves the page and comes back to /admin/login. */
  signInWithGoogle: (returnTo?: string) => Promise<void>;
  /** Sends a 6-digit sign-in code to an existing account (never creates one). */
  sendEmailCode: (email: string) => Promise<void>;
  /** Verifies the 6-digit code typed into the page. */
  verifyEmailCode: (email: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  /** Re-reads the profile and roles (e.g. after editing your own name). */
  reloadUser: () => Promise<void>;
  clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const loadedUidRef = useRef<string | null>(null);

  const clearAuthError = () => {
    setAuthError(null);
  };

  const refreshSession = async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        throw error;
      }
      setSession(data.session);
    } catch (error) {
      console.error("Error refreshing session:", error);
      throw error;
    }
  };

  // Initialize auth state
  useEffect(() => {
    console.info("Initializing AuthProvider");

    // Listen to auth state changes. Profile + roles are only (re)loaded when
    // the signed-in account changes: token refreshes keep the shell mounted.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      console.info("Auth state change:", event);
      setSession(newSession);

      if (newSession?.user) {
        if (loadedUidRef.current !== newSession.user.id) {
          loadedUidRef.current = newSession.user.id;
          setIsLoading(true);
          setAuthError(null);
          // Defer: never await Supabase calls inside the auth callback.
          setTimeout(() => {
            fetchUserData(newSession.user.id);
          }, 0);
        }
      } else {
        loadedUidRef.current = null;
        setUser(null);
        setIsLoading(false);
      }
    });

    // Then check for an existing session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      if (currentSession?.user) {
        if (loadedUidRef.current !== currentSession.user.id) {
          loadedUidRef.current = currentSession.user.id;
          fetchUserData(currentSession.user.id);
        }
      } else {
        setIsLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Separate function to fetch user data to avoid RLS recursion
  const fetchUserData = async (userId: string) => {
    try {
      console.info("Fetching user data for userId:", userId);
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("uid", userId)
        .single();

      if (profileError) {
        console.error("Error fetching user profile:", profileError);

        // Check if the error is specifically because no profile was found
        if (
          profileError.code === "PGRST116" ||
          profileError.message.includes("0 rows")
        ) {
          // This means the user is authenticated but doesn't have an admin profile
          console.error("Profile not found for authenticated user:", userId);
          setAuthError(
            "This account has no reader profile, so it can't have staff access."
          );
          // Don't sign out immediately, let's allow the error to be seen
          // await supabase.auth.signOut();
        } else {
          setAuthError("Failed to load user profile. Please try again.");
        }

        setIsLoading(false);
        return;
      }

      console.info("Profile data fetched successfully:", profileData.name);

      // Create an initial user object with empty roles array
      const userData: User = {
        id: profileData.id,
        uid: profileData.uid,
        name: profileData.name,
        email: profileData.email,
        avatarUrl: profileData.avatar_url,
        isActive: profileData.is_active,
        registeredAt: profileData.registered_at,
        roleIds: [],
        roles: [],
      };

      // Fetch role IDs directly without setTimeout
      try {
        console.info("Fetching user roles for user ID:", profileData.id);
        const { data: userRoleData, error: userRoleError } = await supabase
          .from("user_to_roles")
          .select("role_id")
          .eq("user_id", profileData.id);

        if (userRoleError) {
          console.error("Error fetching user roles:", userRoleError);
        } else {
          const roleIds = userRoleData?.map((r) => r.role_id) || [];
          console.info("User roles fetched:", roleIds);

          // Set role IDs
          userData.roleIds = roleIds;

          // Fetch complete role objects
          if (roleIds.length > 0) {
            const { data: rolesData, error: rolesError } = await supabase
              .from("user_roles")
              .select("*")
              .in("id", roleIds);

            if (rolesError) {
              console.error("Error fetching roles data:", rolesError);
            } else {
              console.info("Role details fetched successfully");
              // Transform to match our UserRole interface
              userData.roles = rolesData.map((role) => ({
                id: role.id,
                name: role.name,
                description: role.description,
                isActive: role.is_active,
                isSystem: role.is_system,
              }));
            }
          }
        }

        // Update user state and finish loading
        console.info("Setting user data in state:", userData.name);
        setUser(userData);
        setIsLoading(false);
      } catch (error) {
        console.error("Error in role fetching process:", error);
        setAuthError("Failed to load user permissions. Please try again.");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error in fetchUserData:", error);
      setAuthError("An unexpected error occurred. Please try again.");
      setIsLoading(false);
    }
  };

  const signInWithGoogle = async (returnTo?: string) => {
    setAuthError(null);
    const redirectTo = new URL("/admin/login", window.location.origin);
    if (returnTo) redirectTo.searchParams.set("next", returnTo);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: redirectTo.toString() },
    });
    if (error) throw error;
  };

  const sendEmailCode = async (email: string) => {
    setAuthError(null);
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim().toLowerCase(),
      // Staff accounts are created by an admin. Never sign up from this page.
      options: { shouldCreateUser: false },
    });
    if (error) throw error;
  };

  const verifyEmailCode = async (email: string, code: string) => {
    setAuthError(null);
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim().toLowerCase(),
      token: code.trim(),
      type: "email",
    });
    if (error) throw error;
  };

  const reloadUser = async () => {
    const {
      data: { session: current },
    } = await supabase.auth.getSession();
    if (current?.user) await fetchUserData(current.user.id);
  };

  const signOut = async () => {
    try {
      // Clear any stored session data first
      localStorage.removeItem("hriatnaeng-auth");

      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
      loadedUidRef.current = null;
      setUser(null);
      setSession(null);
      setAuthError(null);
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  };

  const value = {
    user,
    session,
    isLoading,
    authError,
    signInWithGoogle,
    sendEmailCode,
    verifyEmailCode,
    signOut,
    refreshSession,
    reloadUser,
    clearAuthError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
